import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

const AUDIO_STORAGE_KEY = 'powerGridAudioPreferences';
const MUSIC_SRC = '/Audio/Substation Drift.mp3';

const AUDIO_ASSET_SRC = {
  buttonPrimary: '/Audio/Heavy_industrial_primary.mp3',
  buttonSecondary: '/Audio/button_press_secondary.mp3',
  cop31AmbitiousTargets: '/Audio/Serefina/Ambitious Targets.mp3',
  buildConfirmed: '/Audio/Serefina/Building.mp3',
  cop31Convenes: '/Audio/Serefina/COP 31 Convenes.mp3',
  cop31ModestAgreement: '/Audio/Serefina/Modest Agreement reached.mp3',
  cop31SummitCollapses: '/Audio/Serefina/Summit collapses.mp3',
  disasterReveal: '/Audio/Serefina/Warning.mp3',
  drillBabyDrill: '/Audio/Serefina/Drill Baby Drill.mp3',
  electrify: '/Audio/Serefina/Electrify.mp3',
  economyCrashed: '/Audio/Serefina/Economic Crash.mp3',
  excellentWork: '/Audio/Serefina/Excellent Work.mp3',
  fantastic: '/Audio/Serefina/fantastic.mp3',
  financing: '/Audio/Serefina/Finaicing.mp3',
  labIntroA: '/Audio/Explainers/introA.mp3',
  labIntroB: '/Audio/Explainers/IntroB.mp3',
  labIntroC: '/Audio/Explainers/IntroC.mp3',
  lightningFlicker: '/Audio/flicker.mp3',
  optimising: '/Audio/Serefina/Optimising.mp3',
  overspend: '/Audio/Serefina/Overspend.mp3',
  projectCancelled: '/Audio/Serefina/Project Cancelled.mp3',
  shortfallConfirmed: '/Audio/Serefina/Shortfall confirmed.mp3',
  surplusSecured: '/Audio/Serefina/Surpluss secured.mp3',
  wellDone: '/Audio/Serefina/Well done.mp3'
};

const DEFAULT_PREFERENCES = {
  musicEnabled: false,
  effectsEnabled: true,
  volume: 0.55
};

const AudioContext = createContext(null);
const activeEffectStoppers = new WeakMap();

function clampVolume(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_PREFERENCES.volume;
  return Math.min(1, Math.max(0, n));
}

export function readAudioPreferences(storage = globalThis.localStorage) {
  try {
    const raw = storage?.getItem?.(AUDIO_STORAGE_KEY);
    if (!raw) return DEFAULT_PREFERENCES;
    const parsed = JSON.parse(raw);
    return {
      musicEnabled:
        typeof parsed.musicEnabled === 'boolean'
          ? parsed.musicEnabled
          : DEFAULT_PREFERENCES.musicEnabled,
      effectsEnabled:
        typeof parsed.effectsEnabled === 'boolean'
          ? parsed.effectsEnabled
          : DEFAULT_PREFERENCES.effectsEnabled,
      volume: clampVolume(parsed.volume)
    };
  } catch {
    return DEFAULT_PREFERENCES;
  }
}

export function writeAudioPreferences(
  preferences,
  storage = globalThis.localStorage
) {
  try {
    storage?.setItem?.(AUDIO_STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Audio preferences are optional and should never block play.
  }
}

function getAudioContextConstructor() {
  return globalThis.AudioContext || globalThis.webkitAudioContext;
}

export function clickCueForElement(element) {
  const control = element?.closest?.('button, summary');
  if (!control) return null;
  if (control.disabled || control.getAttribute('aria-disabled') === 'true') {
    return null;
  }

  const configured = control.getAttribute('data-audio-cue');
  if (configured === 'none') return null;
  if (configured) return configured;
  return 'buttonSecondary';
}

function createGain(ctx, destination, value) {
  const gain = ctx.createGain();
  gain.gain.value = value;
  gain.connect(destination);
  return gain;
}

function playOscillatorCue(ctx, destination, volume, steps) {
  if (!ctx || !destination || volume <= 0) return;
  const now = ctx.currentTime;
  const gain = createGain(ctx, destination, 0.0001);
  const osc = ctx.createOscillator();
  osc.type = steps.type ?? 'sine';
  osc.frequency.setValueAtTime(steps.from, now);
  osc.frequency.exponentialRampToValueAtTime(
    Math.max(1, steps.to),
    now + steps.duration
  );
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(volume * steps.level, now + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + steps.duration);
  osc.connect(gain);
  osc.start(now);
  osc.stop(now + steps.duration + 0.03);
}

function playNoiseCue(ctx, destination, volume, duration, level) {
  if (!ctx || !destination || volume <= 0) return;
  const sampleRate = ctx.sampleRate;
  const buffer = ctx.createBuffer(1, Math.max(1, sampleRate * duration), sampleRate);
  const channel = buffer.getChannelData(0);
  for (let i = 0; i < channel.length; i += 1) {
    channel[i] = (Math.random() * 2 - 1) * (1 - i / channel.length);
  }
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  const gain = createGain(ctx, destination, 0.0001);
  const now = ctx.currentTime;
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(volume * level, now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  source.connect(gain);
  source.start(now);
  source.stop(now + duration + 0.03);
}

function playGeneratedCue(ctx, destination, cueName, volume) {
  const restrainedVolume = volume * 0.55;
  switch (cueName) {
    case 'lightningFlicker':
      playNoiseCue(ctx, destination, restrainedVolume, 0.16, 0.35);
      playOscillatorCue(ctx, destination, restrainedVolume, {
        from: 180,
        to: 70,
        duration: 0.22,
        level: 0.18,
        type: 'sawtooth'
      });
      break;
    case 'plantPurchase':
      playOscillatorCue(ctx, destination, restrainedVolume, {
        from: 220,
        to: 520,
        duration: 0.18,
        level: 0.22,
        type: 'triangle'
      });
      break;
    case 'yearAdvance':
      playOscillatorCue(ctx, destination, restrainedVolume, {
        from: 120,
        to: 260,
        duration: 0.28,
        level: 0.18,
        type: 'sine'
      });
      break;
    case 'disasterReveal':
      playNoiseCue(ctx, destination, restrainedVolume, 0.24, 0.28);
      playOscillatorCue(ctx, destination, restrainedVolume, {
        from: 160,
        to: 55,
        duration: 0.36,
        level: 0.15,
        type: 'square'
      });
      break;
    case 'shortfallWarning':
      playOscillatorCue(ctx, destination, restrainedVolume, {
        from: 440,
        to: 330,
        duration: 0.16,
        level: 0.16,
        type: 'square'
      });
      window.setTimeout(() => {
        playOscillatorCue(ctx, destination, restrainedVolume, {
          from: 440,
          to: 330,
          duration: 0.16,
          level: 0.12,
          type: 'square'
        });
      }, 170);
      break;
    case 'awardReveal':
      playOscillatorCue(ctx, destination, restrainedVolume, {
        from: 330,
        to: 660,
        duration: 0.22,
        level: 0.2,
        type: 'triangle'
      });
      break;
    case 'finalScore':
      playOscillatorCue(ctx, destination, restrainedVolume, {
        from: 180,
        to: 360,
        duration: 0.34,
        level: 0.18,
        type: 'sine'
      });
      break;
    case 'leaderboardSubmission':
      playOscillatorCue(ctx, destination, restrainedVolume, {
        from: 520,
        to: 780,
        duration: 0.18,
        level: 0.16,
        type: 'triangle'
      });
      break;
    default:
      break;
  }
}

export async function playAssetCue(cueName, volume, activeAudio = new Set()) {
  const src = AUDIO_ASSET_SRC[cueName];
  if (!src || typeof Audio === 'undefined') return false;
  try {
    const audio = new Audio(src);
    const release = () => {
      activeAudio.delete(audio);
      activeEffectStoppers.delete(audio);
    };
    activeEffectStoppers.set(audio, () => {
      audio.pause?.();
      release();
    });
    activeAudio.add(audio);
    audio.addEventListener('ended', release, { once: true });
    audio.addEventListener('error', release, { once: true });
    audio.preload = 'auto';
    audio.volume = Math.min(1, Math.max(0, volume * 0.9));
    const result = audio.play();
    if (result?.catch) {
      try {
        await result;
      } catch {
        release();
        return false;
      }
    }
    return true;
  } catch {
    return false;
  }
}

export async function playAssetSequence(
  cueNames,
  volume,
  activeAudio = new Set(),
  signal
) {
  if (typeof Audio === 'undefined') return false;
  for (const cueName of cueNames) {
    if (signal?.aborted) return false;
    const src = AUDIO_ASSET_SRC[cueName];
    if (!src) return false;
    const played = await new Promise((resolve) => {
      let settled = false;
      const audio = new Audio(src);
      const finish = (result) => {
        if (settled) return;
        settled = true;
        activeAudio.delete(audio);
        activeEffectStoppers.delete(audio);
        signal?.removeEventListener('abort', abort);
        resolve(result);
      };
      const abort = () => {
        audio.pause?.();
        finish(false);
      };
      activeEffectStoppers.set(audio, abort);
      activeAudio.add(audio);
      audio.preload = 'auto';
      audio.volume = Math.min(1, Math.max(0, volume * 0.9));
      audio.addEventListener('ended', () => finish(true), { once: true });
      audio.addEventListener('error', () => finish(false), { once: true });
      signal?.addEventListener('abort', abort, { once: true });
      try {
        const result = audio.play();
        if (result?.catch) result.catch(() => finish(false));
      } catch {
        finish(false);
      }
    });
    if (!played) return false;
  }
  return true;
}

export function syncActiveEffectAudio(activeAudio, effectsEnabled, volume) {
  const nextVolume = Math.min(1, Math.max(0, volume * 0.9));
  for (const audio of [...activeAudio]) {
    if (!effectsEnabled) {
      activeEffectStoppers.get(audio)?.();
    } else {
      audio.volume = nextVolume;
    }
  }
}

export function AudioProvider({ children }) {
  const [preferences, setPreferences] = useState(() => readAudioPreferences());
  const [unlocked, setUnlocked] = useState(false);
  const [musicAllowed, setMusicAllowed] = useState(false);
  const ctxRef = useRef(null);
  const destinationRef = useRef(null);
  const musicRef = useRef(null);
  const activeEffectsRef = useRef(new Set());
  const preferencesRef = useRef(preferences);

  useEffect(() => {
    preferencesRef.current = preferences;
    writeAudioPreferences(preferences);
  }, [preferences]);

  useEffect(() => {
    syncActiveEffectAudio(
      activeEffectsRef.current,
      preferences.effectsEnabled,
      preferences.volume
    );
  }, [preferences.effectsEnabled, preferences.volume]);

  const ensureAudio = useCallback(async () => {
    const ContextCtor = getAudioContextConstructor();
    if (!ContextCtor) return false;
    try {
      if (!ctxRef.current) {
        const ctx = new ContextCtor();
        ctxRef.current = ctx;
        destinationRef.current = ctx.destination;
      }
      if (ctxRef.current.state === 'suspended') {
        await ctxRef.current.resume();
      }
      setUnlocked(true);
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    function handleFirstInteraction() {
      ensureAudio();
    }
    window.addEventListener('pointerdown', handleFirstInteraction, {
      once: true,
      capture: true
    });
    window.addEventListener('keydown', handleFirstInteraction, {
      once: true,
      capture: true
    });
    return () => {
      window.removeEventListener('pointerdown', handleFirstInteraction, {
        capture: true
      });
      window.removeEventListener('keydown', handleFirstInteraction, {
        capture: true
      });
    };
  }, [ensureAudio]);

  useEffect(() => {
    if (!unlocked || !musicAllowed || !preferences.musicEnabled) {
      if (musicRef.current) {
        musicRef.current.pause();
      }
      return;
    }
    try {
      if (!musicRef.current) {
        const audio = new Audio(MUSIC_SRC);
        audio.loop = true;
        audio.preload = 'auto';
        musicRef.current = audio;
      }
      musicRef.current.volume = preferences.volume * 0.26;
      const result = musicRef.current.play();
      if (result?.catch) result.catch(() => {});
    } catch {
      // Music is decorative, so playback errors are intentionally ignored.
    }
  }, [musicAllowed, preferences.musicEnabled, preferences.volume, unlocked]);

  const updatePreferences = useCallback((patch) => {
    setPreferences((current) => ({
      ...current,
      ...patch,
      volume:
        patch.volume === undefined ? current.volume : clampVolume(patch.volume)
    }));
  }, []);

  const playCue = useCallback(
    async (cueName) => {
      if (!preferencesRef.current.effectsEnabled) return;
      const ready = await ensureAudio();
      if (!ready || !ctxRef.current || !destinationRef.current) return;
      try {
        const assetPlayed = await playAssetCue(
          cueName,
          preferencesRef.current.volume,
          activeEffectsRef.current
        );
        if (assetPlayed) return;
        playGeneratedCue(
          ctxRef.current,
          destinationRef.current,
          cueName,
          preferencesRef.current.volume
        );
      } catch {
        // Sound effects are decorative and should never interrupt input.
      }
    },
    [ensureAudio]
  );

  const playSequence = useCallback(
    async (cueNames, signal) => {
      if (!preferencesRef.current.effectsEnabled) return false;
      const ready = await ensureAudio();
      if (!ready) return false;
      return playAssetSequence(
        cueNames,
        preferencesRef.current.volume,
        activeEffectsRef.current,
        signal
      );
    },
    [ensureAudio]
  );

  useEffect(() => {
    function handleClick(event) {
      const cueName = clickCueForElement(event.target);
      if (!cueName) return;
      playCue(cueName);
    }

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [playCue]);

  const value = useMemo(
    () => ({
      preferences,
      unlocked,
      musicAllowed,
      setMusicAllowed,
      updatePreferences,
      playCue,
      playSequence
    }),
    [musicAllowed, playCue, playSequence, preferences, updatePreferences, unlocked]
  );

  return (
    <AudioContext.Provider value={value}>{children}</AudioContext.Provider>
  );
}

export function useAudio() {
  const value = useContext(AudioContext);
  if (!value) {
    return {
      preferences: DEFAULT_PREFERENCES,
      unlocked: false,
      musicAllowed: false,
      setMusicAllowed: () => {},
      updatePreferences: () => {},
      playCue: () => {},
      playSequence: () => Promise.resolve(false)
    };
  }
  return value;
}
